/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { HeaderSearch, HeaderSearchHandle } from '@/components/content/Header/parts/Search';
import {
	headerMobileBackContainerSX,
	headerMobileCloseSearchIconSX,
	headerMobileDrawerSX,
	headerSearchMobileDrawerContainerSX,
} from '@/components/content/Header/styles/mobileDrawer';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { Box, Drawer, Grid, Stack, Typography } from '@mui/material';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import { HeaderNavBarSuggest } from '@/components/content/Header/parts/HeaderNavBarSuggest';
import { HeaderSearchSuggestProduct } from '@/components/content/Header/parts/HeaderSearchSuggestProduct';
import { useSearchNavigation } from '@/data/Content/SearchNavigation';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

type Option = {
	label: string;
	href?: string;
	identifier: string;
};

const settings = {
	infinite: false,
	speed: 500,
	slidesToShow: 4,
	slidesToScroll: 4,
	arrows: true,
	responsive: [
		{
			breakpoint: 1024,
			settings: {
				slidesToShow: 3,
				slidesToScroll: 3,
			},
		},
		{
			breakpoint: 768,
			settings: {
				slidesToShow: 2,
				slidesToScroll: 2,
			},
		},
		{
			breakpoint: 480,
			settings: {
				slidesToShow: 2,
				slidesToScroll: 2,
			},
		},
	],
};

export const SearchMobileDrawer: FC<{
	toggleSearch: any;
	open: boolean;
	navBarRecommendation: JSX.Element;
}> = ({ toggleSearch, open, navBarRecommendation }) => {
	const router = useNextRouter();
	const [suggest, setSuggest] = useState<any[]>([]);
	const { onSubmit } = useSearchNavigation();
	const headerSearchRef = useRef<HeaderSearchHandle>(null);
	const [searchValueExternal, setSearchValueExternal] = useState<string>('');
	const [suggestProducts, setSuggestProducts] = useState<any[]>([]);

	const [activeSlide, setActiveSlide] = useState(0);
	const sliderRef = useRef<any>(null);
	const [mouseMoved, setMouseMoved] = useState(false);
	const [currentSlidesToShow, setCurrentSlidesToShow] = useState(2);

	// Hook para detectar el breakpoint actual y ajustar slidesToShow
	useEffect(() => {
		const updateSlidesToShow = () => {
			const width = window.innerWidth;
			if (width > 1024) {
				setCurrentSlidesToShow(4);
			} else if (width > 768) {
				setCurrentSlidesToShow(3);
			} else {
				setCurrentSlidesToShow(2);
			}
		};

		updateSlidesToShow();
		window.addEventListener('resize', updateSlidesToShow);

		return () => window.removeEventListener('resize', updateSlidesToShow);
	}, []);

	// Calcular el número total de páginas basado en los productos y slides por página
	const totalPages = useMemo(
		() => Math.ceil(suggestProducts.length / currentSlidesToShow),
		[suggestProducts.length, currentSlidesToShow]
	);

	const options = useMemo<Option[]>(
		() =>
			suggest
				.map(({ identifier, entry }) =>
					identifier && entry
						? entry.map(({ label, href }: { label: string; href?: string }) => ({
								label,
								href,
								identifier,
						  })) || []
						: []
				)
				.flat(1)
				.filter((elm) => elm.identifier === 'Keyword'),
		[suggest]
	);

	const suggestCategories = useMemo<Option[]>(
		() =>
			suggest
				.map(({ identifier, entry }) =>
					identifier && entry
						? entry.map(({ label, href }: { label: string; href: string }) => ({
								label,
								href,
								identifier,
						  })) || []
						: []
				)
				.flat(1)
				.filter((elm) => elm.identifier === 'Category'),
		[suggest]
	);

	useEffect(() => {
		if (!router.asPath) return;
		toggleSearch(false)();
	}, [router.asPath, toggleSearch]);

	const handleSuggestChange = (newSuggest: any) => {
		setSuggest(newSuggest);
	};

	const handleClearSearchFromNavBar = () => {
		if (headerSearchRef.current) {
			headerSearchRef.current.clearSearch();
		}
	};

	const handleSearchSubmit = (value: { label: string }) => {
		onSubmit(value);
		handleClearSearchFromNavBar();
		toggleSearch(false);
	};

	const handleSearchValue = (newSearchValue: any) => {
		setSearchValueExternal(newSearchValue);
	};

	const handleSuggestProductsChange = (newSuggestProducts: any) => {
		setSuggestProducts(newSuggestProducts);
	};

	return (
		<Drawer
			anchor="left"
			open={open}
			onClose={toggleSearch(false)}
			sx={{
				zIndex: 9999,
				width: '100%',
				'.MuiDrawer-paperAnchorLeft': {
					position: 'absolute',
					width: '100%',
				},
			}}
		>
			<Box sx={headerSearchMobileDrawerContainerSX}>
				<Box sx={headerMobileDrawerSX} role="presentation">
					<Box sx={headerMobileBackContainerSX}>
						<HeaderSearch
							id="header-search-input"
							ref={headerSearchRef}
							mobile={true}
							onSuggestChange={handleSuggestChange}
							onSearchValue={handleSearchValue}
							onSuggestProductsChange={handleSuggestProductsChange}
						/>
						<ClearIcon sx={headerMobileCloseSearchIconSX} onClick={toggleSearch(false)} />
					</Box>

					{searchValueExternal.length ? (
						<Stack
							display={'flex'}
							direction={'column'}
							//  spacing={'65px'}
						>
							<Stack display={'flex'} direction={'column'} spacing={1}>
								{options && options.length ? (
									<Typography
										sx={{
											fontSize: 'var(--font-size-body)',
											fontWeight: 'bold',
											color: '#707070',
											letterSpacing: '0px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-start',
											gap: '6px',
										}}
									>
										<SearchIcon sx={{ fontSize: '20px', fontWeight: 'bold' }} />
										{'Sugerencias'}
									</Typography>
								) : null}

								<Stack spacing={2} mt={'14px'}>
									{options && options.length ? (
										<>
											{options.map(({ label }: { label: string }, index: number) => (
												<HeaderNavBarSuggest
													key={index}
													label={label}
													searchMatch={searchValueExternal}
													onClick={() => handleSearchSubmit({ label })}
												/>
											))}
											<HeaderNavBarSuggest
												label="Ver todo"
												sx={{ fontWeight: 'bold' }}
												searchMatch={searchValueExternal}
												onClick={() => handleSearchSubmit({ label: searchValueExternal })}
											/>
										</>
									) : null}
								</Stack>
							</Stack>
							<Stack display={'flex'} direction={'column'} mt={'30px'}>
								<Grid container spacing={2}>
									{headerSearchRef?.current?.isLoading ? (
										<Grid item xs={8} sx={{ backgroundColor: 'transparent' }}>
											<Typography
												sx={{
													fontSize: { xs: 'var(--font-size-md)', md: 'var(--font-size-md)' },
													fontWeight: 'bold',
												}}
											>
												{'Cargando resultados...'}
											</Typography>
										</Grid>
									) : suggestProducts.length === 0 ? (
										<Grid item xs={6} sx={{ backgroundColor: 'transparent' }}>
											<Typography sx={{ fontSize: 'var(--font-size-md)', fontWeight: 'bold' }}>
												{'Sin coincidencias'}
											</Typography>
										</Grid>
									) : null}
								</Grid>
							</Stack>
							{suggestProducts.length > 0 ? (
								<Stack
									display={'flex'}
									direction={'column'}
									mt={{
										xs: options.length ? '30px' : '0px',
										sm: '0px',
									}}
								>
									<Typography
										sx={{
											color: '#707070',
											fontSize: 'var(--font-size-body)',
											fontWeight: 800,
											marginBottom: '5px',
											textTransform: 'uppercase',
										}}
									>
										{'Sugerencias populares'}
									</Typography>
									<Slider
										{...settings}
										{...{ ref: sliderRef }}
										beforeChange={(_, next) => {
											setActiveSlide(Math.floor(next / currentSlidesToShow));
											setMouseMoved(true);
										}}
										afterChange={() => {
											setMouseMoved(false);
										}}
									>
										{
											suggestProducts.map((element, index) => (
												<HeaderSearchSuggestProduct
													key={`id_suggest_product_${index}`}
													product={element}
													isInDrawer
												/>
											)) as any
										}
									</Slider>
									<Box
										sx={{
											width: '90%',
											mx: 'auto',
											display: 'flex',
											direction: 'row',
											borderRadius: '10px',
											backgroundColor: '#EDEDED',
											mt: '20px',
										}}
									>
										{Array.from({ length: totalPages }).map((_, index) => (
											<Box
												key={`id_carousel_dot_${index}`}
												sx={{
													backgroundColor: activeSlide === index ? '#5D5D5D' : '#EDEDED',
													width: `calc(100%/${totalPages})`,
													height: '6px',
													margin: '0px !important',
													padding: '0px !important',

													borderTopLeftRadius: index === 0 ? '10px' : '0px',
													borderBottomLeftRadius: index === 0 ? '10px' : '0px',
													borderTopRightRadius: index === totalPages - 1 ? '10px' : '0px',
													borderBottomRightRadius: index === totalPages - 1 ? '10px' : '0px',
													cursor: 'pointer',
												}}
												onClick={() => {
													sliderRef.current?.slickGoTo(index * currentSlidesToShow);
												}}
											/>
										))}
									</Box>
								</Stack>
							) : null}
						</Stack>
					) : (
						<Fragment>{navBarRecommendation}</Fragment>
					)}
				</Box>
			</Box>
		</Drawer>
	);
};
